import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Paper,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  useTheme,
  Divider,
  IconButton,
  Card,
  CardContent,
  Chip,
  Snackbar,
  Alert,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tabs,
  Tab,
} from "@mui/material";
import {
  SaveOutlined,
  ArrowBackIosNewOutlined,
  DeleteOutlined,
  AddCircleOutlined,
  ImageOutlined,
  CloudUploadOutlined,
  Add,
  Print,
  Edit,
  Delete,
} from "@mui/icons-material";
import { tokens } from "../../theme";
import { getToken, base_url } from "../../config/config";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import Barcode from "react-barcode";

const ProductForm = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  // All state declarations
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [stockOwners, setStockOwners] = useState([]);
  const [stockCategories, setStockCategories] = useState([]);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categoryDialogTab, setCategoryDialogTab] = useState(0);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState("");
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [updatingCategory, setUpdatingCategory] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState(null);
  const [openBarcodeDialog, setOpenBarcodeDialog] = useState(false);
  const [selectedBarcodeProduct, setSelectedBarcodeProduct] = useState(null);
  const [newTag, setNewTag] = useState("");

  const [productData, setProductData] = useState({
    name: "",
    description: "",
    artikelNummer: "",
    unitName: "Stück",
    purchasePrice: 0,
    materialType: "",
    supplierInfo: [],
    stockQuantity: 0,
    minStockLevel: 0,
    location: "stock",
    stockOwner: "",
    stockCategory: "",
    wallThickness: {
      left: "",
      right: "",
      average: "",
      unit: "mm",
    },
    length: "",
    lengthUnit: "mm",
    diameter: "",
    diameterUnit: "mm",
    tags: [],
  });

  // Utility functions
  const convertLength = (value, fromUnit, toUnit) => {
    if (!value || isNaN(value)) return "";
    const numValue = parseFloat(value);

    if (fromUnit === "mm" && toUnit === "m") {
      return (numValue / 1000).toFixed(3);
    } else if (fromUnit === "m" && toUnit === "mm") {
      return (numValue * 1000).toFixed(0);
    }
    return value;
  };

  const getConversionDisplay = (value, unit) => {
    if (!value || isNaN(value)) return "";
    const numValue = parseFloat(value);

    if (unit === "mm") {
      const meters = (numValue / 1000).toFixed(3);
      return `= ${meters}m`;
    } else if (unit === "m") {
      const millimeters = (numValue * 1000).toFixed(0);
      return `= ${millimeters}mm`;
    }
    return "";
  };

  const getWallThicknessConversionDisplay = (value, unit) => {
    if (!value || isNaN(value)) return "";
    const numValue = parseFloat(value);

    if (unit === "mm") {
      const meters = (numValue / 1000).toFixed(6);
      return `= ${meters}m`;
    } else if (unit === "m") {
      const millimeters = (numValue * 1000).toFixed(3);
      return `= ${millimeters}mm`;
    }
    return "";
  };

  const calculateWallThicknessAverage = (left, right) => {
    const leftNum = parseFloat(left) || 0;
    const rightNum = parseFloat(right) || 0;

    if (leftNum > 0 && rightNum > 0) {
      return ((leftNum + rightNum) / 2).toFixed(3);
    } else if (leftNum > 0) {
      return leftNum.toString();
    } else if (rightNum > 0) {
      return rightNum.toString();
    }
    return "";
  };

  // Event handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (["purchasePrice", "stockQuantity", "minStockLevel"].includes(name)) {
      setProductData({
        ...productData,
        [name]: parseFloat(value) || 0,
      });
    } else {
      setProductData({
        ...productData,
        [name]: value,
      });
    }
  };

  const handleLengthChange = (field, value) => {
    if (field === "length") {
      setProductData({
        ...productData,
        length: value,
      });
    } else if (field === "lengthUnit") {
      const convertedValue = convertLength(
        productData.length,
        productData.lengthUnit,
        value
      );
      setProductData({
        ...productData,
        length: convertedValue,
        lengthUnit: value,
      });
    }
  };

  const handleDiameterChange = (field, value) => {
    if (field === "diameter") {
      setProductData({
        ...productData,
        diameter: value,
      });
    } else if (field === "diameterUnit") {
      const convertedValue = convertLength(
        productData.diameter,
        productData.diameterUnit,
        value
      );
      setProductData({
        ...productData,
        diameter: convertedValue,
        diameterUnit: value,
      });
    }
  };

  const handleWallThicknessChange = (field, value) => {
    if (field === "unit") {
      const convertedLeft = convertLength(
        productData.wallThickness.left,
        productData.wallThickness.unit,
        value
      );
      const convertedRight = convertLength(
        productData.wallThickness.right,
        productData.wallThickness.unit,
        value
      );

      const newWallThickness = {
        left: convertedLeft,
        right: convertedRight,
        average: calculateWallThicknessAverage(convertedLeft, convertedRight),
        unit: value,
      };

      setProductData({
        ...productData,
        wallThickness: newWallThickness,
      });
    } else {
      const newWallThickness = {
        ...productData.wallThickness,
        [field]: value,
      };

      if (field === "left" || field === "right") {
        newWallThickness.average = calculateWallThicknessAverage(
          field === "left" ? value : productData.wallThickness.left,
          field === "right" ? value : productData.wallThickness.right
        );
      }

      setProductData({
        ...productData,
        wallThickness: newWallThickness,
      });
    }
  };

  // Tag handlers
  const handleAddTag = () => {
    if (newTag.trim() && !productData.tags.includes(newTag.trim())) {
      setProductData({
        ...productData,
        tags: [...productData.tags, newTag.trim()],
      });
      setNewTag("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setProductData({
      ...productData,
      tags: productData.tags.filter((tag) => tag !== tagToRemove),
    });
  };

  const handleTagKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  // Supplier handlers
  const handleAddSupplier = () => {
    setProductData({
      ...productData,
      supplierInfo: [
        ...productData.supplierInfo,
        {
          firmId: "",
          price: 0,
          leadTime: 0,
        },
      ],
    });
  };

  const handleRemoveSupplier = (index) => {
    const updatedSupplierInfo = [...productData.supplierInfo];
    updatedSupplierInfo.splice(index, 1);
    setProductData({
      ...productData,
      supplierInfo: updatedSupplierInfo,
    });
  };

  const handleSupplierChange = (index, field, value) => {
    const updatedSupplierInfo = [...productData.supplierInfo];
    updatedSupplierInfo[index][field] =
      field === "price" || field === "leadTime"
        ? parseFloat(value) || 0
        : value;

    setProductData({
      ...productData,
      supplierInfo: updatedSupplierInfo,
    });
  };

  // Image handlers
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Barcode handlers
  const handleOpenBarcodeDialog = (product) => {
    setSelectedBarcodeProduct(product);
    setOpenBarcodeDialog(true);
  };

  const handleCloseBarcodeDialog = () => {
    setOpenBarcodeDialog(false);
    setSelectedBarcodeProduct(null);
  };

  const handlePrintBarcode = () => {
    const printContent = document.getElementById("barcode-print");
    const WinPrint = window.open("", "", "width=600,height=600");
    WinPrint.document.write(
      "<html><head><title>Barcode drucken</title></head><body>"
    );
    WinPrint.document.write(printContent.innerHTML);
    WinPrint.document.write("</body></html>");
    WinPrint.document.close();
    WinPrint.focus();
    WinPrint.print();
    WinPrint.close();
  };

  // Category handlers
  const handleOpenCategoryDialog = () => {
    setCategoryDialogOpen(true);
    setCategoryDialogTab(0);
  };

  const handleCloseCategoryDialog = () => {
    setCategoryDialogOpen(false);
    setCategoryDialogTab(0);
    setNewCategoryName("");
    setEditingCategory(null);
    setEditCategoryName("");
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      setAlert({
        open: true,
        message: "Bitte geben Sie einen Kategorienamen ein",
        severity: "error",
      });
      return;
    }

    setCreatingCategory(true);
    try {
      const token = getToken();
      const response = await axios.post(
        `${base_url}/v1/stock-categories/createStockCategory`,
        { name: newCategoryName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setStockCategories([...stockCategories, response.data.data]);
        setProductData({
          ...productData,
          stockCategory: response.data.data._id,
        });
        setNewCategoryName("");
        setAlert({
          open: true,
          message: "Lagerkategorie erfolgreich erstellt",
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Error creating stock category:", error);
      setAlert({
        open: true,
        message:
          error.response?.data?.message ||
          "Fehler beim Erstellen der Lagerkategorie",
        severity: "error",
      });
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleStartEditCategory = (category) => {
    setEditingCategory(category);
    setEditCategoryName(category.name);
  };

  const handleCancelEditCategory = () => {
    setEditingCategory(null);
    setEditCategoryName("");
  };

  const handleUpdateCategory = async () => {
    if (!editCategoryName.trim()) {
      setAlert({
        open: true,
        message: "Bitte geben Sie einen Kategorienamen ein",
        severity: "error",
      });
      return;
    }

    setUpdatingCategory(true);
    try {
      const token = getToken();
      const response = await axios.put(
        `${base_url}/v1/stock-categories/updateStockCategory/${editingCategory._id}`,
        { name: editCategoryName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setStockCategories(
          stockCategories.map((cat) =>
            cat._id === editingCategory._id ? response.data.data : cat
          )
        );
        setEditingCategory(null);
        setEditCategoryName("");
        setAlert({
          open: true,
          message: "Lagerkategorie erfolgreich aktualisiert",
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Error updating stock category:", error);
      setAlert({
        open: true,
        message:
          error.response?.data?.message ||
          "Fehler beim Aktualisieren der Lagerkategorie",
        severity: "error",
      });
    } finally {
      setUpdatingCategory(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (
      !window.confirm(
        "Sind Sie sicher, dass Sie diese Kategorie löschen möchten?"
      )
    ) {
      return;
    }

    setDeletingCategory(categoryId);
    try {
      const token = getToken();
      const response = await axios.delete(
        `${base_url}/v1/stock-categories/deleteStockCategory/${categoryId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setStockCategories(
          stockCategories.filter((cat) => cat._id !== categoryId)
        );

        if (productData.stockCategory === categoryId) {
          setProductData({
            ...productData,
            stockCategory: "",
          });
        }

        setAlert({
          open: true,
          message: "Lagerkategorie erfolgreich gelöscht",
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Error deleting stock category:", error);
      setAlert({
        open: true,
        message:
          error.response?.data?.message ||
          "Fehler beim Löschen der Lagerkategorie",
        severity: "error",
      });
    } finally {
      setDeletingCategory(null);
    }
  };

  const handleCategoryTabChange = (event, newValue) => {
    setCategoryDialogTab(newValue);
    if (newValue === 0) {
      setEditingCategory(null);
      setEditCategoryName("");
    } else {
      setNewCategoryName("");
    }
  };

  // Save product handler
  const handleSaveProduct = async () => {
    if (
      !productData.name ||
      !productData.artikelNummer ||
      !productData.unitName
    ) {
      setAlert({
        open: true,
        message: "Bitte füllen Sie alle Pflichtfelder aus",
        severity: "error",
      });
      return;
    }

    setSaving(true);
    try {
      const token = getToken();

      let imageId = productData.image;
      if (imageFile) {
        const formData = new FormData();
        formData.append("file", imageFile);

        const imageResponse = await axios.post(
          `${base_url}/v1/uploadFile`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (
          imageResponse.data &&
          imageResponse.data.data &&
          imageResponse.data.data._id
        ) {
          imageId = imageResponse.data.data._id;
        } else {
          console.error(
            "Failed to get image ID from response:",
            imageResponse.data
          );
          setAlert({
            open: true,
            message: "Fehler beim Hochladen des Bildes",
            severity: "error",
          });
          setSaving(false);
          return;
        }
      }

      let lengthInMm = "";
      let diameterInMm = "";

      if (productData.length) {
        if (productData.lengthUnit === "m") {
          lengthInMm = (parseFloat(productData.length) * 1000).toString();
        } else {
          lengthInMm = productData.length;
        }
      }

      if (productData.diameter) {
        if (productData.diameterUnit === "m") {
          diameterInMm = (parseFloat(productData.diameter) * 1000).toString();
        } else {
          diameterInMm = productData.diameter;
        }
      }

      const dataToSave = {
        ...productData,
        length: lengthInMm,
        diameter: diameterInMm,
        tags: productData.tags,
        image: imageId,
      };

      delete dataToSave.lengthUnit;
      delete dataToSave.diameterUnit;

      let response;
      if (isEditMode) {
        response = await axios.put(
          `${base_url}/v1/stock/products/${id}`,
          dataToSave,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        response = await axios.post(
          `${base_url}/v1/stock/products`,
          dataToSave,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      if (response.data.success) {
        setAlert({
          open: true,
          message: `Produkt ${
            isEditMode ? "aktualisiert" : "erstellt"
          } erfolgreich`,
          severity: "success",
        });

        if (!isEditMode && response.data.data) {
          const createdProduct = {
            _id: response.data.data._id,
            artikelNummer:
              response.data.data.artikelNummer || productData.artikelNummer,
            name: response.data.data.name || productData.name,
            stockOwner: stockOwners.find(
              (owner) => owner._id === productData.stockOwner
            ),
            diameter: productData.diameter,
            diameterUnit: productData.diameterUnit,
            location: productData.location,
          };

          setTimeout(() => {
            handleOpenBarcodeDialog(createdProduct);
          }, 1000);
        } else {
          setTimeout(() => {
            navigate("/stock/products");
          }, 1500);
        }
      } else {
        setAlert({
          open: true,
          message: response.data.message || "Vorgang fehlgeschlagen",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error saving product:", error);
      setAlert({
        open: true,
        message:
          error.response?.data?.message || "Fehler beim Speichern des Produkts",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  // Helper function to generate barcode value
  const generateBarcodeValue = (productData, stockOwners) => {
    // Get stock owner name
    const stockOwner = stockOwners.find(
      (owner) => owner._id === productData.stockOwner
    );
    const ownerName = stockOwner?.name || "Unknown";

    // Get diameter (convert to mm if needed)
    let diameter = "0";
    if (productData.diameter) {
      if (productData.diameterUnit === "m") {
        diameter = (parseFloat(productData.diameter) * 1000).toString();
      } else {
        diameter = productData.diameter;
      }
    }

    // Get location
    const location = productData.location || "Unknown";

    // Combine the three values with a separator
    return `${ownerName}-${diameter}-${location}`;
  };

  // useEffect
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = getToken();

        const materialTypesResponse = await axios.get(
          `${base_url}/v1/getAllMaterailart`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (materialTypesResponse.data) {
          setMaterialTypes(materialTypesResponse.data);
        }

        const suppliersResponse = await axios.get(`${base_url}/v1/getFirma`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (suppliersResponse.data.data) {
          setSuppliers(suppliersResponse.data.data);
        }

        const stockOwnersResponse = await axios.get(
          `${base_url}/v1/stock/owners`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (stockOwnersResponse.data.success) {
          setStockOwners(stockOwnersResponse.data.data);
        }

        const stockCategoriesResponse = await axios.get(
          `${base_url}/v1/stock-categories/getStockCategory`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (stockCategoriesResponse.data.success) {
          setStockCategories(stockCategoriesResponse.data.data);
        }

        if (isEditMode) {
          const productResponse = await axios.get(
            `${base_url}/v1/stock/products/${id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (productResponse.data.success) {
            const product = productResponse.data.data;
            setProductData({
              name: product.name || "",
              description: product.description || "",
              artikelNummer: product.artikelNummer || "",
              unitName: product.unitName || "Stück",
              purchasePrice: product.purchasePrice || 0,
              materialType: product.materialType?._id || "",
              supplierInfo: product.supplierInfo || [],
              stockQuantity: product.stockQuantity || 0,
              minStockLevel: product.minStockLevel || 0,
              location: product.location || "stock",
              stockOwner: product.stockOwner?._id || "",
              stockCategory: product.stockCategory?._id || "",
              wallThickness: {
                left: product.wallThickness?.left || "",
                right: product.wallThickness?.right || "",
                average: product.wallThickness?.average || "",
                unit: product.wallThickness?.unit || "mm",
              },
              length: product.length || "",
              lengthUnit: "mm",
              diameter: product.diameter || "",
              diameterUnit: "mm",
              tags: product.tags || [],
              image: product.image || null,
            });

            if (product.image) {
              setImagePreview(`${base_url}/uploads/${product.image.filename}`);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setAlert({
          open: true,
          message:
            "Fehler beim Laden der Daten. Bitte versuchen Sie es erneut.",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isEditMode]);

  // Loading state
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Render JSX (rest of your JSX remains the same)
  return (
    <Box m="20px">
      {/* HEADER */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography
            variant="h2"
            color={colors.grey[100]}
            fontWeight="bold"
            mb="5px"
          >
            {isEditMode ? "Produkt bearbeiten" : "Neues Produkt erstellen"}
          </Typography>
          <Typography variant="h5" color={colors.greenAccent[400]}>
            {isEditMode
              ? "Produktdetails aktualisieren"
              : "Ein neues Produkt zum Bestand hinzufügen"}
          </Typography>
        </Box>

        <Box>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ArrowBackIosNewOutlined />}
            onClick={() => navigate("/stock/products")}
            sx={{ mr: 1 }}
          >
            Zurück zu Produkten
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={
              saving ? <CircularProgress size={20} /> : <SaveOutlined />
            }
            onClick={handleSaveProduct}
            disabled={saving}
          >
            {isEditMode ? "Produkt aktualisieren" : "Produkt speichern"}
          </Button>
        </Box>
      </Box>

      {/* PRODUCT FORM */}
      <Paper sx={{ p: 3, bgcolor: colors.primary[400], mt: 2 }}>
        <Grid container spacing={3}>
          {/* MAIN PRODUCT DETAILS */}
          <Grid item xs={12} md={8}>
            <Typography variant="h4" mb={2}>
              Produktinformationen
            </Typography>

            <TextField
              label="Produktname"
              name="name"
              value={productData.name}
              onChange={handleInputChange}
              fullWidth
              required
              variant="filled"
              sx={{ mb: 2 }}
            />

            <TextField
              label="Beschreibung"
              name="description"
              value={productData.description}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={3}
              variant="filled"
              sx={{ mb: 2 }}
            />

            {/* Tags Field - Updated to support multiple tags */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" mb={1}>
                Tags
              </Typography>

              {/* Display existing tags */}
              <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                {productData.tags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    onDelete={() => handleRemoveTag(tag)}
                    color="secondary"
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>

              {/* Add new tag input */}
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={10}>
                  <TextField
                    label="Neuen Tag hinzufügen"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={handleTagKeyPress}
                    fullWidth
                    variant="filled"
                    size="small"
                    helperText="Drücken Sie Enter oder klicken Sie auf + um Tag hinzuzufügen"
                  />
                </Grid>
                <Grid item xs={2}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleAddTag}
                    disabled={!newTag.trim()}
                    startIcon={<Add />}
                    fullWidth
                    sx={{ height: "48px" }}
                  >
                    Tag
                  </Button>
                </Grid>
              </Grid>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Artikelnummer (SKU)"
                  name="artikelNummer"
                  value={productData.artikelNummer}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  variant="filled"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="filled" sx={{ mb: 2 }}>
                  <InputLabel>Einheit</InputLabel>
                  <Select
                    name="unitName"
                    value={productData.unitName}
                    onChange={handleInputChange}
                    required
                  >
                    <MenuItem value="Stück">Stück</MenuItem>
                    <MenuItem value="m">m</MenuItem>
                    <MenuItem value="pschl">pschl</MenuItem>
                    <MenuItem value="kg">kg</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Einkaufspreis (€)"
                  name="purchasePrice"
                  type="number"
                  value={productData.purchasePrice}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  variant="filled"
                  InputProps={{
                    inputProps: { min: 0, step: 0.01 },
                  }}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Aktueller Bestand"
                  name="stockQuantity"
                  type="number"
                  value={productData.stockQuantity}
                  onChange={handleInputChange}
                  fullWidth
                  variant="filled"
                  InputProps={{
                    inputProps: { min: 0 },
                  }}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Mindestbestandslevel"
                  name="minStockLevel"
                  type="number"
                  value={productData.minStockLevel}
                  onChange={handleInputChange}
                  fullWidth
                  variant="filled"
                  InputProps={{
                    inputProps: { min: 0 },
                  }}
                  sx={{ mb: 2 }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth variant="filled" sx={{ mb: 2 }}>
                  <InputLabel>Materialtyp</InputLabel>
                  <Select
                    name="materialType"
                    value={productData.materialType}
                    onChange={handleInputChange}
                  >
                    <MenuItem value="">Keine</MenuItem>
                    {materialTypes.map((type) => (
                      <MenuItem key={type._id} value={type._id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={3}>
                <FormControl fullWidth variant="filled" sx={{ mb: 2 }}>
                  <InputLabel>Lagerkategorie</InputLabel>
                  <Select
                    name="stockCategory"
                    value={productData.stockCategory}
                    onChange={handleInputChange}
                  >
                    <MenuItem value="">Keine Kategorie</MenuItem>
                    {stockCategories.map((category) => (
                      <MenuItem key={category._id} value={category._id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Updated Category Button */}
                {/* <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<Add />}
                  onClick={handleOpenCategoryDialog}
                  sx={{ mt: 1 }}
                >
                  Kategorien verwalten
                </Button> */}
              </Grid>

              <Grid item xs={12} sm={3}>
                <FormControl fullWidth variant="filled" sx={{ mb: 2 }}>
                  <InputLabel>Lagerort</InputLabel>
                  <Select
                    name="location"
                    value={productData.location}
                    onChange={handleInputChange}
                  >
                    <MenuItem value="stock">Hauptlager</MenuItem>
                    <MenuItem value="production">Produktionsbereich</MenuItem>
                    <MenuItem value="quality">Qualitätskontrolle</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={3}>
                <FormControl fullWidth variant="filled" sx={{ mb: 2 }}>
                  <InputLabel>Lagerbesitzer</InputLabel>
                  <Select
                    name="stockOwner"
                    value={productData.stockOwner}
                    onChange={handleInputChange}
                  >
                    <MenuItem value="">Kein Besitzer</MenuItem>
                    {stockOwners.map((owner) => (
                      <MenuItem key={owner._id} value={owner._id}>
                        {owner.name} ({owner.type})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Length and Diameter Section */}
            <Typography variant="h6" mb={2} mt={3}>
              Abmessungen
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={8}>
                      <TextField
                        label="Länge"
                        type="number"
                        value={productData.length}
                        onChange={(e) =>
                          handleLengthChange("length", e.target.value)
                        }
                        fullWidth
                        variant="filled"
                        InputProps={{
                          inputProps: { min: 0, step: 0.001 },
                        }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <FormControl fullWidth variant="filled">
                        <InputLabel>Einheit</InputLabel>
                        <Select
                          value={productData.lengthUnit}
                          onChange={(e) =>
                            handleLengthChange("lengthUnit", e.target.value)
                          }
                        >
                          <MenuItem value="mm">mm</MenuItem>
                          <MenuItem value="m">m</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  {productData.length && (
                    <Typography
                      variant="caption"
                      color={colors.grey[300]}
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      {productData.length}
                      {productData.lengthUnit}{" "}
                      {getConversionDisplay(
                        productData.length,
                        productData.lengthUnit
                      )}
                    </Typography>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={8}>
                      <TextField
                        label="Durchmesser"
                        type="number"
                        value={productData.diameter}
                        onChange={(e) =>
                          handleDiameterChange("diameter", e.target.value)
                        }
                        fullWidth
                        variant="filled"
                        InputProps={{
                          inputProps: { min: 0, step: 0.001 },
                        }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <FormControl fullWidth variant="filled">
                        <InputLabel>Einheit</InputLabel>
                        <Select
                          value={productData.diameterUnit}
                          onChange={(e) =>
                            handleDiameterChange("diameterUnit", e.target.value)
                          }
                        >
                          <MenuItem value="mm">mm</MenuItem>
                          <MenuItem value="m">m</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  {productData.diameter && (
                    <Typography
                      variant="caption"
                      color={colors.grey[300]}
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      {productData.diameter}
                      {productData.diameterUnit}{" "}
                      {getConversionDisplay(
                        productData.diameter,
                        productData.diameterUnit
                      )}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>

            {/* Wall Thickness Section - Updated with unit conversion */}
            <Typography variant="h6" mb={2} mt={3}>
              Wandstärke
            </Typography>

            {/* Unit selector for wall thickness */}
            <Box sx={{ mb: 2 }}>
              <FormControl variant="filled" sx={{ minWidth: 120 }}>
                <InputLabel>Einheit</InputLabel>
                <Select
                  value={productData.wallThickness.unit}
                  onChange={(e) =>
                    handleWallThicknessChange("unit", e.target.value)
                  }
                >
                  <MenuItem value="mm">mm</MenuItem>
                  <MenuItem value="m">m</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Box sx={{ mb: 2 }}>
                  <TextField
                    label="Linke Dicke"
                    type="number"
                    value={productData.wallThickness.left}
                    onChange={(e) =>
                      handleWallThicknessChange("left", e.target.value)
                    }
                    fullWidth
                    variant="filled"
                    InputProps={{
                      inputProps: { min: 0, step: 0.001 },
                    }}
                  />
                  {productData.wallThickness.left && (
                    <Typography
                      variant="caption"
                      color={colors.grey[300]}
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      {productData.wallThickness.left}
                      {productData.wallThickness.unit}{" "}
                      {getWallThicknessConversionDisplay(
                        productData.wallThickness.left,
                        productData.wallThickness.unit
                      )}
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ mb: 2 }}>
                  <TextField
                    label="Rechte Dicke"
                    type="number"
                    value={productData.wallThickness.right}
                    onChange={(e) =>
                      handleWallThicknessChange("right", e.target.value)
                    }
                    fullWidth
                    variant="filled"
                    InputProps={{
                      inputProps: { min: 0, step: 0.001 },
                    }}
                  />
                  {productData.wallThickness.right && (
                    <Typography
                      variant="caption"
                      color={colors.grey[300]}
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      {productData.wallThickness.right}
                      {productData.wallThickness.unit}{" "}
                      {getWallThicknessConversionDisplay(
                        productData.wallThickness.right,
                        productData.wallThickness.unit
                      )}
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ mb: 2 }}>
                  <TextField
                    label="Durchschnittliche Dicke"
                    value={productData.wallThickness.average}
                    fullWidth
                    variant="filled"
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        backgroundColor: colors.grey[800],
                        color: colors.grey[100],
                      },
                    }}
                    helperText="Automatisch berechnet"
                  />
                  {productData.wallThickness.average && (
                    <Typography
                      variant="caption"
                      color={colors.grey[300]}
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      {productData.wallThickness.average}
                      {productData.wallThickness.unit}{" "}
                      {getWallThicknessConversionDisplay(
                        productData.wallThickness.average,
                        productData.wallThickness.unit
                      )}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Grid>

          {/* PRODUCT IMAGE */}
          <Grid item xs={12} md={4}>
            <Typography variant="h4" mb={2}>
              Produktbild
            </Typography>

            <Card sx={{ mb: 3, textAlign: "center" }}>
              <Box
                sx={{
                  height: 200,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: colors.primary[500],
                  position: "relative",
                }}
              >
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Produktvorschau"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <ImageOutlined
                    sx={{ fontSize: 80, color: colors.grey[500] }}
                  />
                )}
              </Box>
              <CardContent>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<CloudUploadOutlined />}
                  fullWidth
                >
                  Bild hochladen
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleImageChange}
                  />
                </Button>
              </CardContent>
            </Card>

            {/* Barcode Section - Show only if product has SKU and is in edit mode */}
            {isEditMode && productData.artikelNummer && (
              <Card sx={{ mb: 3, textAlign: "center" }}>
                <Box sx={{ p: 2 }}>
                  <Typography variant="h6" mb={2}>
                    Barcode
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      bgcolor: "#fff",
                      p: 2,
                      borderRadius: 1,
                    }}
                  >
                    <Barcode
                      value={generateBarcodeValue(productData, stockOwners)}
                    />
                  </Box>
                  <Typography
                    variant="caption"
                    color={colors.grey[300]}
                    sx={{ mt: 1, display: "block" }}
                  >
                    Barcode: {generateBarcodeValue(productData, stockOwners)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color={colors.grey[400]}
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    SKU: {productData.artikelNummer}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Print />}
                      onClick={() =>
                        handleOpenBarcodeDialog({
                          _id: id,
                          artikelNummer: productData.artikelNummer,
                          name: productData.name,
                          stockOwner: stockOwners.find(
                            (owner) => owner._id === productData.stockOwner
                          ),
                          diameter: productData.diameter,
                          diameterUnit: productData.diameterUnit,
                          location: productData.location,
                        })
                      }
                    >
                      Barcode drucken
                    </Button>
                  </Box>
                </Box>
              </Card>
            )}

            <Typography variant="h5" mb={2}>
              Produktdetails
            </Typography>

            {isEditMode && (
              <Box mb={2}>
                <Chip
                  label={`Gesamtwert: €${(
                    productData.stockQuantity * productData.purchasePrice
                  ).toFixed(2)}`}
                  color="secondary"
                  sx={{ mb: 1, mr: 1 }}
                />
                {productData.odooId && (
                  <Chip
                    label={`Odoo ID: ${productData.odooId}`}
                    sx={{ mb: 1 }}
                  />
                )}
              </Box>
            )}

            {productData.stockOwner && (
              <Typography
                variant="body2"
                color={colors.grey[300]}
                sx={{ mb: 1 }}
              >
                Besitzer:{" "}
                {stockOwners.find(
                  (owner) => owner._id === productData.stockOwner
                )?.name || "Unbekannt"}
              </Typography>
            )}

            <Typography variant="body2" color={colors.grey[300]} sx={{ mb: 1 }}>
              Zuletzt aktualisiert:{" "}
              {isEditMode
                ? new Date(productData.updatedAt).toLocaleString()
                : "Noch nicht gespeichert"}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* SUPPLIER INFORMATION */}
      <Paper sx={{ p: 3, bgcolor: colors.primary[400], mt: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h4"></Typography>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<AddCircleOutlined />}
            onClick={handleOpenCategoryDialog}
            sx={{ mt: 1 }}
          >
            Kategorie hinzufügen
          </Button>
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h4">Lieferanteninformationen</Typography>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<AddCircleOutlined />}
            onClick={handleAddSupplier}
          >
            Lieferant hinzufügen
          </Button>
        </Box>

        {productData.supplierInfo.length === 0 ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            bgcolor={colors.primary[500]}
            p={3}
            borderRadius={1}
          >
            <Typography color={colors.grey[400]}>
              Noch keine Lieferanten hinzugefügt. Klicken Sie auf 'Lieferant
              hinzufügen', um Lieferanteninformationen hinzuzufügen.
            </Typography>
          </Box>
        ) : (
          productData.supplierInfo.map((supplier, index) => (
            <Box
              key={index}
              mb={2}
              p={2}
              bgcolor={colors.primary[500]}
              borderRadius={1}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={5}>
                  <FormControl fullWidth variant="filled">
                    <InputLabel>Lieferant</InputLabel>
                    <Select
                      value={supplier.firmId}
                      onChange={(e) =>
                        handleSupplierChange(index, "firmId", e.target.value)
                      }
                    >
                      {suppliers.map((firm) => (
                        <MenuItem key={firm._id} value={firm._id}>
                          {firm.bezeichnung}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Preis (€)"
                    type="number"
                    value={supplier.price}
                    onChange={(e) =>
                      handleSupplierChange(index, "price", e.target.value)
                    }
                    fullWidth
                    variant="filled"
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Lieferzeit (Tage)"
                    type="number"
                    value={supplier.leadTime}
                    onChange={(e) =>
                      handleSupplierChange(index, "leadTime", e.target.value)
                    }
                    fullWidth
                    variant="filled"
                    InputProps={{
                      inputProps: { min: 0 },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={1}>
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveSupplier(index)}
                  >
                    <DeleteOutlined />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>
          ))
        )}
      </Paper>

      {/* ENHANCED STOCK CATEGORY DIALOG */}
      <Dialog
        open={categoryDialogOpen}
        onClose={handleCloseCategoryDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h4" component="div">
            Lagerkategorien verwalten
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Tabs
            value={categoryDialogTab}
            onChange={handleCategoryTabChange}
            sx={{ mb: 3 }}
          >
            <Tab label="Neue Kategorie erstellen" />
            <Tab label="Kategorien verwalten" />
          </Tabs>

          {/* Tab 0: Create New Category */}
          {categoryDialogTab === 0 && (
            <Box>
              <TextField
                autoFocus
                margin="dense"
                label="Kategoriename"
                fullWidth
                variant="filled"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleCreateCategory();
                  }
                }}
                sx={{ mt: 2 }}
              />
              <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                <Button
                  onClick={handleCreateCategory}
                  variant="contained"
                  color="secondary"
                  disabled={creatingCategory || !newCategoryName.trim()}
                  startIcon={
                    creatingCategory ? <CircularProgress size={20} /> : <Add />
                  }
                >
                  Kategorie erstellen
                </Button>
              </Box>
            </Box>
          )}

          {/* Tab 1: Manage Categories */}
          {categoryDialogTab === 1 && (
            <Box>
              <Typography variant="h6" mb={2}>
                Vorhandene Kategorien ({stockCategories.length})
              </Typography>

              {stockCategories.length === 0 ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  p={3}
                  bgcolor={colors.primary[500]}
                  borderRadius={1}
                >
                  <Typography color={colors.grey[400]}>
                    Keine Kategorien vorhanden. Erstellen Sie eine neue
                    Kategorie.
                  </Typography>
                </Box>
              ) : (
                <List>
                  {stockCategories.map((category) => (
                    <ListItem
                      key={category._id}
                      sx={{
                        bgcolor: colors.primary[500],
                        mb: 1,
                        borderRadius: 1,
                        border:
                          productData.stockCategory === category._id
                            ? `2px solid ${colors.greenAccent[400]}`
                            : "none",
                      }}
                    >
                      {editingCategory &&
                      editingCategory._id === category._id ? (
                        // Edit mode
                        <Box
                          sx={{
                            display: "flex",
                            width: "100%",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <TextField
                            value={editCategoryName}
                            onChange={(e) =>
                              setEditCategoryName(e.target.value)
                            }
                            onKeyPress={(e) => {
                              if (e.key === "Enter") {
                                handleUpdateCategory();
                              } else if (e.key === "Escape") {
                                handleCancelEditCategory();
                              }
                            }}
                            variant="outlined"
                            size="small"
                            sx={{ flexGrow: 1 }}
                            autoFocus
                          />
                          <Button
                            size="small"
                            onClick={handleUpdateCategory}
                            disabled={updatingCategory}
                            variant="contained"
                            color="secondary"
                          >
                            {updatingCategory ? (
                              <CircularProgress size={16} />
                            ) : (
                              "Speichern"
                            )}
                          </Button>
                          <Button
                            size="small"
                            onClick={handleCancelEditCategory}
                            variant="outlined"
                          >
                            Abbrechen
                          </Button>
                        </Box>
                      ) : (
                        // View mode
                        <>
                          <ListItemText
                            primary={category.name}
                            secondary={
                              productData.stockCategory === category._id
                                ? "Aktuell ausgewählt"
                                : `Erstellt: ${new Date(
                                    category.createdAt
                                  ).toLocaleDateString()}`
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              aria-label="edit"
                              onClick={() => handleStartEditCategory(category)}
                              sx={{ mr: 1 }}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              onClick={() => handleDeleteCategory(category._id)}
                              disabled={deletingCategory === category._id}
                              color="error"
                            >
                              {deletingCategory === category._id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <Delete />
                              )}
                            </IconButton>
                          </ListItemSecondaryAction>
                        </>
                      )}
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseCategoryDialog} color="secondary">
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* BARCODE DIALOG - Same as ListAnfrage implementation */}
      <Dialog
        open={openBarcodeDialog}
        onClose={handleCloseBarcodeDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h4" component="div">
            Produkt Barcode
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedBarcodeProduct && (
            <Box
              id="barcode-print"
              sx={{
                textAlign: "center",
                p: 3,
                bgcolor: "#fff",
                color: "#000",
                borderRadius: 1,
              }}
            >
              <Typography variant="h5" fontWeight="bold" mb={2}>
                {selectedBarcodeProduct.name}
              </Typography>
              <Box
                sx={{
                  border: "2px solid #000",
                  p: 2,
                  mb: 2,
                  display: "inline-block",
                  borderRadius: 1,
                }}
              >
                <Barcode
                  value={generateBarcodeValue(
                    {
                      stockOwner: selectedBarcodeProduct.stockOwner?._id,
                      diameter: selectedBarcodeProduct.diameter,
                      diameterUnit: selectedBarcodeProduct.diameterUnit || "mm",
                      location: selectedBarcodeProduct.location,
                    },
                    stockOwners
                  )}
                />
              </Box>
              <Typography variant="body1" fontWeight="bold" mb={1}>
                Barcode:{" "}
                {generateBarcodeValue(
                  {
                    stockOwner: selectedBarcodeProduct.stockOwner?._id,
                    diameter: selectedBarcodeProduct.diameter,
                    diameterUnit: selectedBarcodeProduct.diameterUnit || "mm",
                    location: selectedBarcodeProduct.location,
                  },
                  stockOwners
                )}
              </Typography>
              <Typography variant="body2" color="textSecondary" mb={1}>
                Besitzer:{" "}
                {selectedBarcodeProduct.stockOwner?.name || "Unbekannt"} •
                Durchmesser: {selectedBarcodeProduct.diameter || "0"}{" "}
                {selectedBarcodeProduct.diameterUnit || "mm"} • Standort:{" "}
                {selectedBarcodeProduct.location === "stock"
                  ? "Hauptlager"
                  : selectedBarcodeProduct.location === "production"
                  ? "Produktionsbereich"
                  : selectedBarcodeProduct.location === "quality"
                  ? "Qualitätskontrolle"
                  : selectedBarcodeProduct.location}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                SKU: {selectedBarcodeProduct.artikelNummer} • ID:{" "}
                {selectedBarcodeProduct._id}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseBarcodeDialog} color="secondary">
            Schließen
          </Button>
          <Button
            onClick={handlePrintBarcode}
            startIcon={<Print />}
            variant="contained"
            color="secondary"
          >
            Drucken
          </Button>
          <Button
            onClick={() => navigate("/stock/products")}
            variant="outlined"
            color="secondary"
          >
            Zu Produktliste
          </Button>
        </DialogActions>
      </Dialog>

      {/* ALERT SNACKBAR */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductForm;
